import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { Icon } from '../atoms/Icon';

export const MenuContainerStyled = styled(Box)(({ theme }) => ({
  display: 'flex',
  backgroundColor: theme.palette.colors.menuBackground,
}));

export const FontAwesomeIconStyled = styled(FontAwesomeIcon, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'opened',
})(({ theme, selected, opened }) => {
  const setColor = () =>
    selected
      ? theme.palette.colors.iconsSelectedOnMenu
      : theme.palette.colors.iconsOnMenuBackground;

  return {
    color: setColor(),
    fontSize: '22px',
    justifyContent: opened ? null : 'center',
  };
});
export const ListItemIconStyled = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'opened',
})(({ opened }) => {
  return {
    justifyContent: opened ? null : 'center',
    minWidth: 0,
  };
});

export const RootListItemButtonStyled = styled(ListItemButton, {
  shouldForwardProp: (prop) =>
    prop !== 'selected' &&
    prop !== 'opened' &&
    prop !== 'isOpen' &&
    prop !== 'isActiveNavigationMenuItemEmpty',
})(({ theme, selected, opened, isOpen, isActiveNavigationMenuItemEmpty }) => {
  const setColor = () =>
    selected
      ? theme.palette.colors.iconsSelectedOnMenu
      : theme.palette.colors.iconsOnMenuBackground;
  const setBackgroundColor = () =>
    opened || (selected && isActiveNavigationMenuItemEmpty)
      ? theme.palette.colors.hoverMenuBackground
      : theme.palette.colors.menuBackground;
  const setBorderColor = () =>
    selected ? theme.palette.colors.selectedBarOnMenu : 'transparent';
  return {
    color: setColor(),
    borderRight: `3px solid ${setBorderColor()}`,
    padding: isOpen ? '0 0 0 24px' : 0,
    justifyContent: !isOpen && 'center',
    '&.MuiButtonBase-root': {
      backgroundColor: setBackgroundColor(),
      height: isOpen ? '40px' : '56px',
      marginTop: isOpen ? '32px' : '16px',
      '&:first-child': {
        marginTop: '8px',
      },
      '&#btnMenuSettings': {
        marginTop: '24px',
      },
      '&#btnMenuLogout': {
        marginBottom: isOpen ? '40px' : '24px',
      },
      '&:hover': {
        backgroundColor: theme.palette.colors.hoverMenuBackground,
      },
      '&::selected': {
        backgroundColor: setBackgroundColor(),
      },
      flexDirection: isOpen ? 'row' : 'column',
      gap: isOpen && '24px',
      [`@media screen and (min-height: ${theme.verticalBreakpoints.xl})`]: {
        marginTop: isOpen ? '40px' : '24px',
        '&:first-child': {
          marginTop: '28px',
        },
        '&#btnMenuSettings': {
          marginTop: '40px',
        },
      },
    },
    '.MuiListItemText-root': {
      marginTop: '3px',
      marginBottom: 0,
      flex: 'none',
    },
    '.MuiListItemText-primary': {
      fontSize: isOpen ? 18 : 10,
      fontWeight: selected ? 'bold' : 500,
      lineHeight: 'normal',
      letterSpacing: 'normal',
    },
  };
});

export const ListStyled = styled(List, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'opened',
})(({ theme, selected, opened }) => {
  const setColor = () =>
    selected
      ? theme.palette.colors.highlight
      : theme.palette.colors.iconsOnMenuBackground;
  const setBackgroundColor = () =>
    selected || opened
      ? theme.palette.colors.hoverMenuBackground
      : theme.palette.colors.menuBackground;
  const setBorderColor = () =>
    selected ? theme.palette.colors.selectedBarOnMenu : 'transparent';

  return {
    color: setColor(),
    fontSize: 12,
    backgroundColor: setBackgroundColor(),
    borderRight: `3px solid ${setBorderColor()}`,
  };
});

export const LeafListItemButtonStyled = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})(({ isOpen }) => ({
  marginLeft: isOpen ? '70px' : '6px',
  marginRight: isOpen ? '24px' : '6px',
  padding: '16px 0 8px',

  '&:not(:last-child)': {
    padding: '8px 0',
  },
  '&:last-child': {
    padding: '8px 0 16px',
  },
  color: 'transparent',
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&.MuiButtonBase-root.Mui-selected': {
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  '& .MuiListItemText-root': {
    margin: 0,
  },
}));

export const LeafListItemTextStyled = styled(ListItemText, {
  shouldForwardProp: (prop) =>
    prop !== 'selected' && prop !== 'isOpen' && prop !== 'isLogoutOption',
})(({ theme, selected, isOpen, isLogoutOption }) => {
  const setColor = () =>
    selected
      ? theme.palette.colors.selectedTextButtonMenu
      : theme.palette.colors.menuBackground;
  const setBackgroundColor = () =>
    selected
      ? theme.palette.colors.hoverMenuBackground
      : theme.palette.colors.menuBackground;
  return {
    color: theme.palette.colors.onIconsAndTextButtonsVariant,
    borderRadius: '5px',
    backgroundColor: setColor(),
    display: 'flex',
    justifyContent: 'center',
    marginLeft: isOpen ? '62px' : '0',
    padding: '5px 0',
    '&.MuiButtonBase-root': {
      backgroundColor: setBackgroundColor(),
      padding: '8px',
      '&.Mui-selected': {
        backgroundColor: setBackgroundColor(),
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
    '&.MuiButtonBase-root.Mui-selected': {
      backgroundColor: 'transparent',
      padding: '8px',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    '.MuiListItemText-primary': {
      fontSize: isLogoutOption ? '10px' : '12px',
      fontWeight: selected ? 'bold' : 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
    },
  };
});

const drawerWidth = 217;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  backgroundColor: theme.palette.colors.menuBackground,
  border: 'none',
  boxShadow: '2px -2px 6px 0 rgba(0, 0, 0, 0.5)',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  backgroundColor: theme.palette.colors.menuBackground,
  border: 'none',
  width: 70,
  [theme.breakpoints.up('sm')]: {
    width: 70,
  },
});
export const DrawerHeaderStyled = styled('div')(({ theme, open }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.colors.menuBackground,
  justifyContent: 'flex-start',
  margin: open ? '24px auto 37px' : '24px auto 37px',
  width: open && '193px',
  [theme.breakpoints.up('sm')]: {
    minHeight: 0,
  },

  [`@media screen and (min-height: ${theme.verticalBreakpoints.xl})`]: {
    margin: '40px auto 30px',
  },
  ...theme.mixins.toolbar,
}));

export const DrawerStyled = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

export const IconButtonStyled = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.colors.iconsOnMenuBackground,
  fontSize: '12px',
  color: theme.palette.colors.onIconsAndTextButtons,
  height: '24px',
  width: '24px',
  position: 'relative',
  float: 'right',
  top: '-12px',
  '&:hover': {
    backgroundColor: theme.palette.colors.mainFocused,
  },
}));

export const IconStyled = styled(Icon)(() => ({
  width: 'auto',
  height: '46px',
  minHeight: 'auto',
}));

export const IconExpandedStyled = styled(Icon)(() => ({
  width: 'auto',
  height: '46px',
  minHeight: 'auto',
  maxWidth: '100%',
}));
export const ListContainerStyled = styled(List, {
  shouldForwardProp: (prop) => prop !== 'listHeight',
})(({ theme, listHeight }) => ({
  backgroundColor: theme.palette.colors.menuBackground,
  color: theme.palette.colors.iconsOnMenuBackground,
  paddingTop: 0,
  paddingBottom: 0,
  height: `calc(100vh - ${listHeight}px)`,
  overflowY: 'auto',

  '&::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
}));
